import CoPresentIcon from '@mui/icons-material/CoPresent';
import { Button as MaterialButton, MenuItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { EngagementCreationPath, EngagementType } from '@/__generated__/graphql';
import Button from '@/components/Button/Button';
import Dialog from '@/components/Dialog';
import FAIcon from '@/components/Icon/FAIcon';
import MaterialIcon from '@/components/Icon/MaterialIcon';
import IconMenu from '@/components/IconMenu';
import Link from '@/components/Link';
import Picture from '@/components/Picture';
import SendMessageButton from '@/components/SendMessageButton';
import { UpgradeRequiredDialog } from '@/components/UpgradeRequiredDialog';
import { getStateDescription } from '@/consultation';
import { ER_TYPE_LABELS } from '@/expertrequest/store';
import { useActiveAccount } from '@/features/consulting-engagements/featureFlag';
import { isEngagementsEnabledForGroup } from '@/features/consulting-engagements/gate';
import HireExpertDrawer from '@/features/consulting-engagements/live/HireExpertDrawer';
import { resolveCreateAffordance } from '@/features/consulting-engagements/live/create-denial';
import {
  HIRE_ACTION_LABEL,
  HIRE_DRAWER_TITLE,
} from '@/features/consulting-engagements/live/hireCopy';
import { usePermissions } from '@/hooks/useAppContext';

import { Consultation } from '../Consultation';
import s from './Header.module.scss';
import Title from './Title';

const engagementTypeLabels = {
  [EngagementType.Consultation]: 'Call',
  [EngagementType.WrittenResponse]: 'Written Response',
  [EngagementType.Opportunity]: 'Opportunity Call',
};

interface ConsultationActionsProps {
  onDelete?: () => void;
}

const ConsultationActions = ({ onDelete }: ConsultationActionsProps) => {
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  const openDeleteConfirmation = () => {
    setDeleteConfirmation(true);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  if (!onDelete) return null;

  return (
    <div className={s.actions}>
      <IconMenu iconSize={24} iconStyle={{ opacity: 1 }}>
        <MenuItem onClick={openDeleteConfirmation}>Delete</MenuItem>
      </IconMenu>
      <Dialog
        open={deleteConfirmation}
        title="Are you sure?"
        subTitle={
          'You will lose all details and audio from this consultation. ' +
          'Click delete to confirm.'
        }
        onCancel={() => setDeleteConfirmation(false)}
        onConfirm={handleDelete}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

interface HeaderProps {
  consultation: Consultation;
  hire: boolean;
  user: any;
  expertId: string;
  isViewerExpert: boolean;
  isViewerAdmin: boolean;
  isCompleted: boolean;
  isExpired: boolean;
  isMobileVersion: boolean;
  callActions: any;
  canHireExpert?: boolean;
  onReviewCall: () => void;
  onDelete?: () => void;
  shouldReview: boolean;
}

const Header = ({
  consultation,
  user,
  hire,
  isViewerExpert,
  isViewerAdmin,
  isCompleted,
  isExpired,
  isMobileVersion,
  callActions,
  canHireExpert = true,
  onReviewCall,
  onDelete,
  shouldReview,
}: HeaderProps): JSX.Element => {
  const { expert, expert_request: expertRequest } = consultation;
  const requesterId = consultation.requester?.id;
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  /**
   * Consulting engagements replace the legacy long-term hire (ENG-5452).
   *
   * The button stays where it is; only what it opens changes. The legacy dialog
   * filed a Typeform interest form for a human to follow up (removed in
   * ENG-6301), which is what this feature made unnecessary:
   * `post_consultation` creates the engagement directly, with the expert and
   * the project both taken from this consultation.
   *
   * **The gate is the api's answer, not a rule computed here (ENG-5727).**
   * `Project.viewer_engagement_create_denial` is null when the viewer may
   * create, and otherwise names what is missing. Deriving it locally is what
   * caused the bug: this surface used to ask whether the OBJECT's account had
   * the product and then exclude every `viewer.admin` outright, while the
   * profile entry point asked whether the VIEWER was acting in an entitled
   * account. Same viewer, same account, two answers — and this one silently
   * fell back to the Typeform interest form, which reads as the feature being
   * broken rather than refused.
   *
   * The staff exclusion is gone because it was both too blunt and unenforced.
   * Too blunt: it also blocked a staff member who is a genuine member of the
   * account and is impersonating nobody. Unenforced: the api's create policy is
   * granted to staff by the global `*:*` wildcard regardless of resource, so
   * this line in a React component was the only thing standing between staff
   * and a billable engagement on any customer account. The rule now lives in
   * `engagement/service/create_authority.go` and this reads its verdict.
   *
   * Three outcomes, and the difference matters:
   *
   * - **allowed** — open the drawer.
   * - **hidden** — the account does not have the product. Render no engagement
   *   affordance at all; do not advertise something the customer has not
   *   bought. If the viewer reaches the action anyway (the legacy
   *   `canHireExpert` button, or the `?hire=true` link in the receipt email),
   *   it opens the upgrade dialog (ENG-6301). Only Enterprise accounts have
   *   engagements, so that covers SMB and an Enterprise account whose
   *   engagements were switched off: a prompt to upgrade, never an error and
   *   never the retired Typeform interest form.
   * - **refused** — the account HAS the product but this viewer may not act on
   *   it. Explain, naming the account or project. Never the Typeform form: a
   *   refusal on an entitled account is a refusal, not a downgrade to the old
   *   interest form.
   */
  const navigate = useNavigate();
  const engagementProject = consultation.expert_request?.project;
  const engagementProjectId = engagementProject?.id;
  const createAffordance = resolveCreateAffordance(
    engagementProject?.viewer_engagement_create_denial,
    {
      accountName: engagementProject?.group?.name,
      projectName: engagementProject?.name,
    }
  );
  // A project id is structurally required by `createEngagement`, so its absence
  // is "no affordance" rather than a refusal to explain.
  const canCreateEngagement = createAffordance.kind === 'allowed' && !!engagementProjectId;
  // ENG-6301. A call with no expert request has no project, so there is no
  // per-project answer to read. The consultation's own account decides, and the
  // drawer asks which project to hire under; its pre-submission check then runs
  // the ENG-5727 refusal against the project picked. The picker lists the
  // projects of the account the viewer is ACTING in, so this holds only when
  // that is the call's account: otherwise the engagement would be billed to
  // another account, or (staff with no account context) the drawer has no
  // picker at all.
  const activeAccount = useActiveAccount();
  const canHireWithoutProject =
    !engagementProjectId &&
    isEngagementsEnabledForGroup(consultation.group) &&
    activeAccount?.id === consultation.group?.id;
  const opensHireDrawer = canCreateEngagement || canHireWithoutProject;
  const engagementRefusal =
    createAffordance.kind === 'refused' && engagementProjectId ? createAffordance.message : null;
  const [engagementRefusalOpen, setEngagementRefusalOpen] = useState(false);
  const [hireDrawerOpen, setHireDrawerOpen] = useState(false);

  /**
   * `?hire=1` is the deep link the hire emails point at, and it has to route the
   * same way a click does.
   *
   * Intercepting only the click handler left the param opening the LEGACY dialog
   * on an allowlisted account: the customer following the email CTA landed on
   * the Typeform interest form this ticket exists to replace, and the drawer
   * never mounted. Routed once on arrival rather than in render, so dismissing
   * either surface does not immediately reopen it while the param is still on
   * the URL.
   */
  useEffect(() => {
    if (!hire) return;
    if (opensHireDrawer) setHireDrawerOpen(true);
    else if (engagementRefusal) setEngagementRefusalOpen(true);
    else setUpgradeDialogOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- arrival only; see above.
  }, [hire]);

  const [canStartNonAnonymousChat, canStartAnonymousChat] = usePermissions(
    [
      { service: 'chat', action: 'start_non_anonymous_chat', resource: requesterId || 'dummy' },
      { service: 'chat', action: 'start_anonymous_chat', resource: requesterId || 'dummy' },
    ],
    [requesterId]
  );

  const stateDescription = getStateDescription(consultation);
  const showMessageButton = !!(
    // must be a valid, non admin, user
    (
      !isViewerAdmin &&
      user &&
      user.username &&
      requesterId &&
      (canStartNonAnonymousChat || canStartAnonymousChat)
    )
  );

  const engagementType = isViewerAdmin ? engagementTypeLabels[consultation.engagement_type] : null;

  const erType =
    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    isViewerAdmin && expertRequest ? ER_TYPE_LABELS[expertRequest.er_type] : null;

  const handleOpenHireforExternalDialog = (): void => {
    if (opensHireDrawer) {
      setHireDrawerOpen(true);
      return;
    }
    // ENG-5727: an entitled account refusing THIS viewer gets an explanation.
    // A viewer with no engagement affordance at all is offered the upgrade
    // (ENG-6301), which replaced the legacy Typeform interest form.
    if (engagementRefusal) {
      setEngagementRefusalOpen(true);
      return;
    }
    setUpgradeDialogOpen(true);
  };

  return (
    <div className={s.header}>
      {user && (
        <div className={s.photo}>
          <Picture user={user} size={64} />
        </div>
      )}

      <div className={s.headerInfo}>
        <div className={s.status}>
          <FAIcon
            className={s.statusIcon}
            iconSet={stateDescription.iconSet}
            icon={stateDescription.icon}
            color={stateDescription.color}
          />
          <span style={{ color: stateDescription.color }} className={s.statusText}>
            {stateDescription.text}
          </span>
          <ConsultationActions onDelete={onDelete} />
        </div>

        {consultation.requester?.name && consultation.expert?.name && (
          <div className={s.name}>
            <Title
              consultation={consultation}
              isViewerExpert={isViewerExpert}
              isViewerRequester={!isViewerExpert}
              requesterName={consultation.requester?.name}
              expertName={consultation.expert?.name}
              isWrittenConsultation={
                consultation.engagement_type === EngagementType.WrittenResponse
              }
            />
          </div>
        )}

        {engagementType && <div className={s.engagementType}>{engagementType}</div>}

        {erType && <div className={s.erType}>{erType}</div>}

        {showMessageButton && (
          <SendMessageButton
            userId={user.id}
            style={{ marginTop: 5, marginLeft: isMobileVersion ? 0 : -10 }}
          />
        )}
      </div>

      {(isCompleted || (isExpired && !isViewerExpert) || (!isMobileVersion && !!callActions)) && (
        <div className={s.headerActions}>
          {!isMobileVersion && callActions}

          {shouldReview && (
            <Button
              fullWidth={isMobileVersion}
              size="large"
              style={{ marginBottom: 10 }}
              onClick={onReviewCall}
            >
              Leave a review
            </Button>
          )}
          {(isCompleted || isExpired) && !isViewerExpert && expert && (
            <div className="flex flex-col">
              <Link
                to={
                  expertRequest
                    ? `${expert.html_url}/request?expertRequestId=${expertRequest.id}`
                    : `${expert.html_url}/request`
                }
              >
                <MaterialButton
                  startIcon={<MaterialIcon icon="cached" style={{ fontSize: 22 }} />}
                  style={{ fontSize: 16 }}
                >
                  Request Again
                </MaterialButton>
              </Link>
              {isCompleted && (canHireExpert || canCreateEngagement || engagementRefusal) && (
                <MaterialButton
                  startIcon={<CoPresentIcon />}
                  style={{ fontSize: 16 }}
                  onClick={handleOpenHireforExternalDialog}
                >
                  {/* One name for the action (ENG-5739), shared with the
                      drawer this opens and with the profile sidebar CTA.
                      Shown unconditionally, so an account without
                      engagements reads it too and is offered the upgrade
                      (ENG-6301). */}
                  <span className="w-max">{HIRE_ACTION_LABEL}</span>
                </MaterialButton>
              )}
            </div>
          )}
        </div>
      )}
      {hireDrawerOpen && opensHireDrawer ? (
        <HireExpertDrawer
          open
          onClose={() => setHireDrawerOpen(false)}
          source={{
            creationPath: EngagementCreationPath.PostConsultation,
            originatingConsultationId: consultation.id,
            projectId: engagementProjectId,
            // The drawer names the expert rather than re-reading them
            // (ENG-5734).
            expertName: expert?.name ?? undefined,
          }}
          onCreated={(engagementId) => {
            setHireDrawerOpen(false);
            navigate(`/engagements/${encodeURIComponent(engagementId)}`);
          }}
        />
      ) : null}
      {/* ENG-5727. A Dialog rather than an inline banner because the refusal is
          feedback on an action just taken, and it replaces a dialog. */}
      <Dialog
        open={engagementRefusalOpen}
        title={HIRE_DRAWER_TITLE}
        subTitle={<p>{engagementRefusal}</p>}
        confirmLabel="Close"
        onConfirm={() => setEngagementRefusalOpen(false)}
        // Escape and backdrop dismissal. `DialogProps` passes MUI's `onClose`
        // straight through, and other Dialog callers in the repo supply it;
        // without it a purely informational dialog can only be left by finding
        // the one button, which is a keyboard trap.
        onClose={() => setEngagementRefusalOpen(false)}
      />
      <UpgradeRequiredDialog
        open={upgradeDialogOpen}
        feature="Expert engagements"
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </div>
  );
};

export default Header;
